/**
 * MESSAGING SERVICE
 *
 * Core business logic for the messaging system. Handles all Firestore operations
 * for conversations and messages.
 *
 * Key Functions:
 * - getOrCreateConversation: Creates or retrieves conversation between two users
 * - sendMessage: Sends a new message and updates conversation metadata
 * - sendInquiry: Starts a conversation about marketplace items
 * - addItemsToConversation: Adds items mid-conversation
 * - updateItemStatus: Seller marks item as sold/pending
 * - subscribeToMessages: Real-time message updates
 * - subscribeToConversations: Real-time conversation list updates
 *
 * Data Structure:
 * - conversations collection: Main conversation documents
 * - conversations/:id/messages: Subcollection of messages
 * - Conversation ID is deterministic (sorted userIds joined by "_")
 *
 * Message Types Handled:
 * - text: Regular chat messages
 * - inquiry: Initial inquiry about items
 * - item_added/creation_added: System messages when items added
 * - status_update: System messages when seller updates status
 * - item_removed/creation_removed: System messages when items removed
 *
 * Technical Notes:
 * - Uses serverTimestamp for consistent timestamps across clients
 * - Implements optimistic updates for lastMessage preview
 * - Caches participant info in conversation for fast rendering
 * - Prevents duplicate pinned items with Set-based deduplication
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { Conversation, Message, AttachedItem, AttachedCreation, ParticipantInfo } from './types';

/**
 * Generate a deterministic conversation ID for two users
 *
 * Creates a consistent ID by sorting user IDs alphabetically and joining with underscore.
 * This ensures the same two users always get the same conversation ID, preventing duplicates.
 *
 * Example:
 *   getConversationId('user456', 'user123') => 'user123_user456'
 *   getConversationId('user123', 'user456') => 'user123_user456'
 *
 * @param userId1 - First user's ID
 * @param userId2 - Second user's ID
 * @returns Deterministic conversation ID
 */
export function getConversationId(userId1: string, userId2: string): string {
  const sorted = [userId1, userId2].sort();
  return `${sorted[0]}_${sorted[1]}`;
}

/**
 * Get or create a conversation between two users
 *
 * First checks if a conversation already exists. If not, creates a new one.
 * Participant info (display name, avatar, username) is cached in the conversation
 * document for fast rendering without additional user lookups.
 *
 * Flow:
 * 1. Generate deterministic conversation ID
 * 2. Check if conversation document exists
 * 3. If exists: return ID
 * 4. If not: create new conversation with participant info and empty pinned items
 *
 * @param currentUserId - Current user's ID
 * @param otherUserId - Other participant's ID
 * @param currentUserInfo - Current user's display info (name, avatar)
 * @param otherUserInfo - Other user's display info
 * @returns Conversation ID (existing or newly created)
 */
export async function getOrCreateConversation(
  currentUserId: string,
  otherUserId: string,
  currentUserInfo: ParticipantInfo,
  otherUserInfo: ParticipantInfo
): Promise<string> {
  const conversationId = getConversationId(currentUserId, otherUserId);
  const conversationRef = doc(db, 'conversations', conversationId);

  const existing = await getDoc(conversationRef);

  if (existing.exists()) {
    return conversationId;
  }

  // Create new conversation
  const newConversation: Omit<Conversation, 'id'> = {
    participants: [currentUserId, otherUserId],
    participantInfo: {
      [currentUserId]: currentUserInfo,
      [otherUserId]: otherUserInfo,
    },
    pinnedItems: [],
    pinnedCreations: [],
    lastMessage: null,
    lastReadAt: {},
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(conversationRef, newConversation);
  return conversationId;
}

/**
 * Delete a conversation, but only if it has no user-authored text.
 * "User-authored" means messages of type 'text' or 'inquiry'. System
 * messages (item_added, item_removed, status_update, creation_added, etc.)
 * are excluded because they're auto-generated when users click around and
 * shouldn't lock a conversation into permanence.
 *
 * On success, cascade-deletes all messages in the subcollection first so
 * nothing orphans, then deletes the conversation doc itself.
 */
export async function deleteEmptyConversation(
  conversationId: string,
  callerUserId: string
): Promise<void> {
  const ref = doc(db, 'conversations', conversationId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Conversation not found');
  const data = snap.data() as Conversation;
  if (!data.participants?.includes(callerUserId)) {
    throw new Error('Not a participant of this conversation');
  }

  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const messagesSnap = await getDocs(messagesRef);
  // Block-list approach: only known system-message types (added items,
  // removed items, status changes) allow deletion. Everything else —
  // including messages with a missing/unknown `type` — is treated as user
  // content and blocks deletion. Safer than the reverse.
  const SYSTEM_TYPES = new Set([
    'item_added',
    'creation_added',
    'status_update',
    'item_removed',
    'creation_removed',
  ]);
  const hasUserContent = messagesSnap.docs.some((m) => {
    const type = (m.data() as { type?: string }).type;
    return !type || !SYSTEM_TYPES.has(type);
  });
  if (hasUserContent) {
    throw new Error('Conversation contains real messages and cannot be deleted.');
  }

  if (messagesSnap.size > 0) {
    let batch = writeBatch(db);
    let ops = 0;
    for (const m of messagesSnap.docs) {
      batch.delete(m.ref);
      ops++;
      if (ops >= 450) {
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
    }
    if (ops > 0) await batch.commit();
  }

  await deleteDoc(ref);
}

/**
 * Send a message in a conversation
 */
export async function sendMessage(
  conversationId: string,
  message: Omit<Message, 'id' | 'timestamp'>
): Promise<void> {
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const conversationRef = doc(db, 'conversations', conversationId);
  
  // Add the message
  await addDoc(messagesRef, {
    ...message,
    timestamp: serverTimestamp(),
  });
  
  // Update conversation's lastMessage
  await updateDoc(conversationRef, {
    lastMessage: {
      text: message.type === 'text' ? message.text : getMessagePreview(message),
      senderId: message.senderId,
      timestamp: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Get a preview text for non-text messages
 */
function getMessagePreview(message: Omit<Message, 'id' | 'timestamp'>): string {
  switch (message.type) {
    case 'inquiry':
      const itemCount = (message.attachedItems?.length || 0) + (message.attachedCreations?.length || 0);
      return `📬 Inquiry about ${itemCount} item${itemCount !== 1 ? 's' : ''}`;
    case 'item_added':
      return `➕ Added items to conversation`;
    case 'creation_added':
      return `✨ Added creations to conversation`;
    case 'status_update':
      return `🏷️ Updated item status`;
    case 'item_removed':
      return `➖ Removed item from conversation`;
    case 'creation_removed':
      return `➖ Removed creation from conversation`;
    default:
      return message.text || 'New message';
  }
}

/**
 * Send an inquiry message (starting a conversation about items)
 *
 * Used when a buyer wants to inquire about a seller's items. This is typically the
 * first message in a conversation.
 *
 * Flow:
 * 1. Get or create conversation between buyer and seller
 * 2. Add items to conversation's pinnedItems (visible in sidebar)
 * 3. Send inquiry message with attached items
 * 4. Return conversation ID for navigation
 *
 * The items are "pinned" to the conversation so they're always visible in the UI,
 * making it easy to reference what's being discussed.
 *
 * @param currentUserId - Buyer's user ID
 * @param sellerId - Seller's user ID
 * @param currentUserInfo - Buyer's display info
 * @param sellerInfo - Seller's display info
 * @param items - Items buyer is inquiring about
 * @param messageText - Inquiry message text
 * @returns Conversation ID
 */
export async function sendInquiry(
  currentUserId: string,
  sellerId: string,
  currentUserInfo: ParticipantInfo,
  sellerInfo: ParticipantInfo,
  items: AttachedItem[],
  messageText: string
): Promise<string> {
  const conversationId = getConversationId(currentUserId, sellerId);
  const conversationRef = doc(db, 'conversations', conversationId);

  // Check if conversation exists
  const existing = await getDoc(conversationRef);

  if (existing.exists()) {
    // Existing conversation - use normal flow
    const existingPinned = existing.data()?.pinnedItems || [];

    // Merge new items with existing (avoid duplicates)
    const existingIds = new Set(existingPinned.map((item: AttachedItem) => item.itemId));
    const newItems = items.filter(item => !existingIds.has(item.itemId));

    if (newItems.length > 0) {
      await updateDoc(conversationRef, {
        pinnedItems: [...existingPinned, ...newItems],
        updatedAt: serverTimestamp(),
      });
    }

    // Send the inquiry message
    await sendMessage(conversationId, {
      senderId: currentUserId,
      type: 'inquiry',
      text: messageText,
      attachedItems: items,
      readBy: [currentUserId],
    });
  } else {
    // New conversation - create conversation first, then message
    const newConversation: Omit<Conversation, 'id'> = {
      participants: [currentUserId, sellerId],
      participantInfo: {
        [currentUserId]: currentUserInfo,
        [sellerId]: sellerInfo,
      },
      pinnedItems: items,
      pinnedCreations: [],
      lastMessage: {
        text: messageText,
        senderId: currentUserId,
        timestamp: new Date(),
      },
      lastReadAt: { [currentUserId]: new Date() },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Create conversation first
    await setDoc(conversationRef, newConversation);

    // Now create the first message (conversation exists now)
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    await addDoc(messagesRef, {
      senderId: currentUserId,
      type: 'inquiry',
      text: messageText,
      attachedItems: items,
      readBy: [currentUserId],
      timestamp: serverTimestamp(),
    });
  }

  return conversationId;
}

/**
 * Add items to an existing conversation
 */
export async function addItemsToConversation(
  conversationId: string,
  senderId: string,
  items: AttachedItem[]
): Promise<void> {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const existingPinned = conversationSnap.data()?.pinnedItems || [];
  
  // Merge new items with existing (avoid duplicates)
  const existingIds = new Set(existingPinned.map((item: AttachedItem) => item.itemId));
  const newItems = items.filter(item => !existingIds.has(item.itemId));
  
  if (newItems.length > 0) {
    await updateDoc(conversationRef, {
      pinnedItems: [...existingPinned, ...newItems],
      updatedAt: serverTimestamp(),
    });
    
    // Send item_added message
    await sendMessage(conversationId, {
      senderId,
      type: 'item_added',
      text: '',
      attachedItems: newItems,
      readBy: [senderId],
    });
  }
}

/**
 * Remove an item from a conversation
 */
export async function removeItemFromConversation(
  conversationId: string,
  senderId: string,
  itemId: string,
  itemName: string
): Promise<void> {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const pinnedItems = conversationSnap.data()?.pinnedItems || [];
  
  // Filter out the removed item
  const updatedPinned = pinnedItems.filter((item: AttachedItem) => item.itemId !== itemId);
  
  // Only update if something was actually removed
  if (updatedPinned.length < pinnedItems.length) {
    await updateDoc(conversationRef, {
      pinnedItems: updatedPinned,
      updatedAt: serverTimestamp(),
    });
    
    // Send item_removed message
    await sendMessage(conversationId, {
      senderId,
      type: 'item_removed',
      text: '',
      statusChange: { itemId, itemName, oldStatus: '', newStatus: 'removed' },
      readBy: [senderId],
    });
  }
}

/**
 * Add creations to an existing conversation
 */
export async function addCreationsToConversation(
  conversationId: string,
  senderId: string,
  creations: AttachedCreation[]
): Promise<void> {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const existingPinned = conversationSnap.data()?.pinnedCreations || [];
  
  // Merge new creations with existing (avoid duplicates)
  const existingIds = new Set(existingPinned.map((c: AttachedCreation) => c.creationId));
  const newCreations = creations.filter(c => !existingIds.has(c.creationId));
  
  if (newCreations.length > 0) {
    await updateDoc(conversationRef, {
      pinnedCreations: [...existingPinned, ...newCreations],
      updatedAt: serverTimestamp(),
    });
    
    // Send creation_added message
    await sendMessage(conversationId, {
      senderId,
      type: 'creation_added',
      text: '',
      attachedCreations: newCreations,
      readBy: [senderId],
    });
  }
}

/**
 * Remove a creation from a conversation
 */
export async function removeCreationFromConversation(
  conversationId: string,
  senderId: string,
  creationId: string,
  creationTitle: string
): Promise<void> {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const pinnedCreations = conversationSnap.data()?.pinnedCreations || [];
  
  // Filter out the removed creation
  const updatedPinned = pinnedCreations.filter((c: AttachedCreation) => c.creationId !== creationId);
  
  // Only update if something was actually removed
  if (updatedPinned.length < pinnedCreations.length) {
    await updateDoc(conversationRef, {
      pinnedCreations: updatedPinned,
      updatedAt: serverTimestamp(),
    });
    
    // Send creation_removed message
    await sendMessage(conversationId, {
      senderId,
      type: 'creation_removed',
      text: '',
      statusChange: { creationId, itemName: creationTitle, oldStatus: '', newStatus: 'removed' },
      readBy: [senderId],
    });
  }
}

/**
 * Update an item's status and notify in conversation
 */
export async function updateItemStatus(
  conversationId: string,
  senderId: string,
  itemId: string,
  itemName: string,
  oldStatus: string,
  newStatus: string
): Promise<void> {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const pinnedItems = conversationSnap.data()?.pinnedItems || [];
  
  // Update status in pinned items
  const updatedPinned = pinnedItems.map((item: AttachedItem) => 
    item.itemId === itemId ? { ...item, status: newStatus } : item
  );
  
  await updateDoc(conversationRef, {
    pinnedItems: updatedPinned,
    updatedAt: serverTimestamp(),
  });
  
  // Send status_update message
  await sendMessage(conversationId, {
    senderId,
    type: 'status_update',
    text: '',
    statusChange: { itemId, itemName, oldStatus, newStatus },
    readBy: [senderId],
  });
}

/**
 * Update a creation's status and notify in conversation
 */
export async function updateCreationStatus(
  conversationId: string,
  senderId: string,
  creationId: string,
  creationTitle: string,
  oldStatus: string,
  newStatus: string
): Promise<void> {
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const pinnedCreations = conversationSnap.data()?.pinnedCreations || [];
  
  // Update status in pinned creations
  const updatedPinned = pinnedCreations.map((creation: AttachedCreation) => 
    creation.creationId === creationId ? { ...creation, status: newStatus } : creation
  );
  
  await updateDoc(conversationRef, {
    pinnedCreations: updatedPinned,
    updatedAt: serverTimestamp(),
  });
  
  // Send status_update message
  await sendMessage(conversationId, {
    senderId,
    type: 'status_update',
    text: '',
    statusChange: { creationId, itemName: creationTitle, oldStatus, newStatus },
    readBy: [senderId],
  });
}

/**
 * Mark conversation as read for a user
 * Updates the lastReadAt timestamp for this user
 */
export async function markMessagesAsRead(
  conversationId: string,
  userId: string
): Promise<void> {
  try {
    const conversationRef = doc(db, 'conversations', conversationId);
    
    await updateDoc(conversationRef, {
      [`lastReadAt.${userId}`]: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
}

/**
 * Subscribe to messages in a conversation (real-time)
 */
export function subscribeToMessages(
  conversationId: string,
  callback: (messages: Message[]) => void
): () => void {
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const q = query(messagesRef, orderBy('timestamp', 'asc'));
  
  return onSnapshot(q, (snapshot) => {
    const messages: Message[] = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Message))
      // Hide messages an admin has flagged. isHidden is written by the
      // ModerationQueue when a report is actioned; participants don't see
      // the message on either side. Legacy messages without the field
      // continue to render as normal.
      .filter((m) => !(m as unknown as { isHidden?: boolean }).isHidden);
    callback(messages);
  });
}

/**
 * Subscribe to user's conversations (real-time)
 */
export function subscribeToConversations(
  userId: string,
  callback: (conversations: Conversation[]) => void
): () => void {
  const conversationsRef = collection(db, 'conversations');
  const q = query(
    conversationsRef,
    where('participants', 'array-contains', userId),
    orderBy('updatedAt', 'desc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const conversations: Conversation[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Conversation));
    callback(conversations);
  });
}

/**
 * Get user info for messaging
 */
export async function getUserInfo(userId: string): Promise<ParticipantInfo> {
  const userDoc = await getDoc(doc(db, 'users', userId));
  
  if (userDoc.exists()) {
    const data = userDoc.data();
    return {
      displayName: data.displayName || data.username || 'Unknown User',
      avatarUrl: data.profile?.avatarUrl || data.avatarUrl || null,
      username: data.username || null,
    };
  }
  
  return {
    displayName: 'Unknown User',
    avatarUrl: null,
    username: null,
  };
}